import { useState, type FC } from "react";
import { Spin } from "antd";

import { Button, LanguageSelect } from "../shared";
import { useRegulatorSignOut } from "../../queries/regulator-portal/useRegulatorSignOut";
import { clearRegulatorPortalAndRedirect } from "../../utils/tokenUtils";
import {
  getStoredGuestLanguage,
  setStoredGuestLanguage,
} from "../../utils/i18n/languagePreference";
import { applyI18nLanguage } from "../../utils/i18n/platformLanguage";
import {
  DEFAULT_LANGUAGE,
  canonicalizeLanguageTag,
} from "../../utils/i18n/supportedLanguages";
import { BRAND_ICONS_URLS } from "../../utils/Constants";
import { AppStrings } from "../../utils/constants/AppStrings";

import styles from "./RegulatorPortalScreen.module.css";

const BRAND_LOGO_SRC = `${process.env.REACT_APP_ADMIN_PANEL_CLOUDFRONT_URL ?? ""}${BRAND_ICONS_URLS.topLeftAppIcon}`;

type RegulatorPortalPendingProps = {
  /** `resolving` is `/me` in flight; `pending` is awaiting operator approval. */
  variant?: "pending" | "resolving";
};

/**
 * Holding screen for a regulator whose `/me` status is `pending` (or whose
 * `/me` has not settled yet). Approval is granted operator-side;
 * `useRegulatorMe` keeps polling while this renders, so the guard swaps this
 * out for the portal without a re-login.
 *
 * This is the first screen a fresh regulator lands on, so it carries its own
 * language switcher (guest key + in-place TCM apply, same as set-password).
 */
const RegulatorPortalPending: FC<RegulatorPortalPendingProps> = ({
  variant = "pending",
}) => {
  const signOut = useRegulatorSignOut();
  const isResolving = variant === "resolving";
  const [, setLanguageTick] = useState(0);

  const activeLanguage =
    canonicalizeLanguageTag(getStoredGuestLanguage()) ?? DEFAULT_LANGUAGE;

  const handleLanguageChange = async (lng: string) => {
    if (!lng || lng === activeLanguage) return;
    setStoredGuestLanguage(lng);
    await applyI18nLanguage(lng);
    setLanguageTick((tick) => tick + 1);
  };

  // A pending regulator holds a valid access token, so the server revocation
  // is as legitimate here as in the portal shell; teardown runs regardless.
  const handleSignOutClicked = () => {
    signOut.mutate(undefined, {
      onSettled: () => {
        clearRegulatorPortalAndRedirect();
      },
    });
  };

  return (
    <div
      className={`${styles.screen} ${styles.fullPage}`}
      data-testid={
        isResolving ? "regulator-portal-loading" : "regulator-portal-pending"
      }
    >
      <div className={styles.brandRow}>
        <span className={styles.logo}>
          <img
            className={styles.logoImage}
            src={BRAND_LOGO_SRC}
            alt={AppStrings.REBET}
            width={22}
            height={22}
          />
        </span>
        <span className={styles.brand}>{AppStrings.PAM_REGULATOR_PORTAL}</span>
      </div>
      <Spin size="large" />
      {isResolving ? (
        <p className={styles.body}>
          {AppStrings.PAM_REGULATOR_PORTAL_CHECKING_ACCESS}
        </p>
      ) : (
        <>
          <h1 className={styles.title}>
            {AppStrings.PAM_REGULATOR_PORTAL_PENDING_TITLE}
          </h1>
          <p className={styles.body}>
            {AppStrings.PAM_REGULATOR_PORTAL_PENDING_BODY}
          </p>
          <p className={styles.hint}>
            {AppStrings.PAM_REGULATOR_PORTAL_PENDING_HINT}
          </p>
        </>
      )}
      <div className={styles.actions}>
        <LanguageSelect
          className={styles.language}
          value={activeLanguage}
          onChange={handleLanguageChange}
          data-testid={
            isResolving
              ? "regulator-portal-loading-language"
              : "regulator-portal-pending-language"
          }
        />
        <Button
          intent="neutral"
          onClick={handleSignOutClicked}
          disabled={signOut.isPending}
          data-testid={
            isResolving
              ? "regulator-portal-loading-logout"
              : "regulator-portal-pending-logout"
          }
        >
          {AppStrings.SIGN_OUT}
        </Button>
      </div>
    </div>
  );
};

export default RegulatorPortalPending;
